import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { config } from '../config';
import axiosInstance from '../api/axios';
import { keycloak } from '../providers/KeycloakAuthProvider';
import { API, CLIENT, SESSIONS } from '../api/endpoints';
import { enqueueSnackbar } from 'notistack';
import { convertEditorStateToTranscript } from '../shared/DataConverters';
import { EditorState } from 'draft-js';

type UseSummaryProps = {
  sessionId: string | null;
  textContent?: string;
  editorState?: EditorState;
  sessionLockKey?: string | null;
};

export type GenerateSummaryRequest = {
  model: string;
  prompt?: string;
  prompt_id?: string;
  variables: {
    [key: string]: any;
  };
  stream: boolean;
  unwrap_text: boolean;
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
  presence_penalty?: number;
  frequency_penalty?: number;
  stop?: string;
  n?: number;
  response_format?: {
    [key: string]: any;
  };
  user?: string;
  seed?: number;
  logit_bias?: {
    [key: string]: number;
  };
  system?: string;
  extra_messages?: Array<{
    [key: string]: string;
  }>;
  conversation_id?: string;
};

export type Prompt = {
  id: string;
  namen: string;
  content: string;
  visible: boolean;
  is_private: boolean;
  is_default: boolean;
};

export type SummaryResponse = {
  content: string;
  config: {
    model: string;
    promptId: string;
  };
};

export type SummarizePayload = {
  model: string;
  promptId: string;
};

export const availableSummaryModels = ['gpt-5', 'gpt-4o-mini'];

export function useSummary({ sessionId, textContent, editorState, sessionLockKey }: UseSummaryProps) {
  // State management
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [summaryInProgress, setSummaryInProgress] = useState(false);
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryExists, setSummaryExists] = useState(false);
  const [streamingText, setStreamingText] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const summaryFetchedRef = useRef<boolean>(false);

  // Service URLs
  const serviceUrl = config.summaryServiceUrl;
  const backendUrl: string = config.backendApiUrl;
  const baseUrl = `${backendUrl}/${API}/${CLIENT}`;

  // Fetch available prompts
  const fetchPrompts = useCallback(async () => {
    try {
      setIsLoadingPrompts(true);

      const authHeader = keycloak.token ? `Bearer ${keycloak.token}` : '';

      const response = await fetch(`${serviceUrl}/v1/prompts?include_content=false`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader && { Authorization: authHeader }),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch prompt IDs');
      }
      const data = await response.json();
      const filteredData = data.filter((prompt: Prompt) => prompt.visible) as Prompt[];
      const sortedData = filteredData.sort((a, b) => a.namen.localeCompare(b.namen));
      setAvailablePrompts(sortedData);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      enqueueSnackbar('Napaka pri pridobivanju predlog za povzemanje.', { variant: 'error' });
    } finally {
      setIsLoadingPrompts(false);
    }
  }, [serviceUrl]);

  // Fetch existing summary and check if it exists
  const fetchSummary = useCallback(async () => {
    if (!sessionId || summaryFetchedRef.current) {
      return;
    }

    try {
      setIsLoadingSummary(true);
      summaryFetchedRef.current = true;

      const response = await axiosInstance.get(`${baseUrl}/${SESSIONS}/${sessionId}/summary`);
      const data = response.data;
      setSummary(data.content);
      setSummaryExists(true);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      setSummaryExists(false);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [sessionId, baseUrl]);

  // Start summary generation with streaming
  const startSummarize = useCallback(
    async (payload: SummarizePayload) => {
      if (!sessionId && !textContent) {
        enqueueSnackbar('Ni vsebine za povzemanje', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      try {
        setIsPending(true);
        setSummaryInProgress(true);
        setStreamingText('');

        abortControllerRef.current = new AbortController();

        // Get content to summarize - either from textContent prop, editorState, or fetch from session
        let contentToSummarize = textContent || '';

        if (editorState && !textContent) {
          // Convert editor state to transcript format
          const { transcript } = convertEditorStateToTranscript(editorState, { toLiveWordData: true });
          contentToSummarize = transcript.map((word) => word.text).join(' ');
        } else if (sessionId && !textContent && !editorState) {
          // If we have a sessionId but no textContent or editorState, fetch the session content
          try {
            const response = await axiosInstance.get(`${baseUrl}/${SESSIONS}/${sessionId}/content`);
            contentToSummarize = response.data.content || '';
          } catch (error) {
            console.error('Failed to fetch session content:', error);
            enqueueSnackbar('Napaka pri pridobivanju vsebine seje.', { variant: 'error' });
            setIsPending(false);
            setSummaryInProgress(false);
            return;
          }
        }
        const url = `${serviceUrl}/v1/generate`;
        const requestData: GenerateSummaryRequest = {
          model: payload.model,
          prompt_id: payload.promptId,
          variables: {
            transkript: contentToSummarize,
          },
          stream: true,
          unwrap_text: false,
        };

        const authHeader = keycloak.token ? `Bearer ${keycloak.token}` : '';

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
            Connection: 'keep-alive',
            ...(authHeader && { Authorization: authHeader }),
          },
          body: JSON.stringify(requestData),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check if response is actually streaming
        const contentType = response.headers.get('content-type');
        const isStreaming = contentType?.includes('text/event-stream') || contentType?.includes('text/plain');

        if (!isStreaming) {
          // Handle non-streaming response
          const responseText = await response.text();
          setSummary(responseText);
          setSummaryExists(true);
          setSummaryInProgress(false);
          enqueueSnackbar('Povzetek je bil uspešno ustvarjen.', { variant: 'success' });

          try {
            if (sessionId) {
              await axiosInstance.put(
                `${baseUrl}/${SESSIONS}/${sessionId}/summary`,
                {
                  content: responseText,
                  config: {
                    model: payload.model,
                    promptId: payload.promptId,
                  },
                },
                {
                  headers: sessionLockKey ? { 'Session-Lock-Key': sessionLockKey } : {},
                }
              );
            }
          } catch (saveError) {
            console.error('Failed to save summary:', saveError);
          }
          return;
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        let timeoutId: NodeJS.Timeout | null = null;

        try {
          // Set up timeout for streaming (30 seconds)
          timeoutId = setTimeout(() => {
            reader.cancel();
            throw new Error('Streaming timeout - response took too long');
          }, 30000);

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);

                if (data === '[DONE]') {
                  clearTimeout(timeoutId!);
                  setSummary(fullText);
                  setStreamingText('');
                  setSummaryExists(true);
                  setSummaryInProgress(false);
                  enqueueSnackbar('Povzetek je bil uspešno ustvarjen.', { variant: 'success' });

                  try {
                    if (sessionId) {
                      await axiosInstance.put(
                        `${baseUrl}/${SESSIONS}/${sessionId}/summary`,
                        {
                          content: fullText,
                          config: {
                            model: payload.model,
                            promptId: payload.promptId,
                          },
                        },
                        {
                          headers: sessionLockKey ? { 'Session-Lock-Key': sessionLockKey } : {},
                        }
                      );
                    } else {
                      console.error('Session ID is missing');
                    }
                  } catch (saveError) {
                    console.error('Failed to save summary:', saveError);
                  }
                  return;
                }

                try {
                  const parsed = JSON.parse(data);
                  if (
                    parsed.choices &&
                    parsed.choices[0] &&
                    parsed.choices[0].delta &&
                    parsed.choices[0].delta.content
                  ) {
                    const content = parsed.choices[0].delta.content;
                    fullText += content;
                    setStreamingText((prev) => prev + content);
                  }
                } catch (parseError) {
                  console.warn('Failed to parse stream chunk:', parseError);
                }
              }
            }
          }

          clearTimeout(timeoutId!);
          setSummary(fullText);
          setStreamingText('');
          setSummaryExists(true);
          setSummaryInProgress(false);
          enqueueSnackbar('Povzetek je bil uspešno ustvarjen.', { variant: 'success' });
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          reader.releaseLock();
        }
      } catch (error) {
        console.error('Failed to start summary generation:', error);
        setSummaryInProgress(false);
        setStreamingText('');

        // Provide more specific error messages
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            enqueueSnackbar('Povzemanje je bilo prekinjeno.', { variant: 'info' });
          } else if (error.message.includes('timeout')) {
            enqueueSnackbar('Povzemanje je trajalo predolgo. Poskusite znova.', { variant: 'error' });
          } else {
            enqueueSnackbar('Prišlo je do napake pri povzemanju.', { variant: 'error' });
          }
        } else {
          enqueueSnackbar('Prišlo je do napake pri povzemanju.', { variant: 'error' });
        }
      } finally {
        setIsPending(false);
      }
    },
    [sessionId, textContent, editorState, sessionLockKey, serviceUrl, baseUrl]
  );

  // Initialize the hook
  const initSummarize = useCallback(() => {
    fetchPrompts();
    if (sessionId) {
      fetchSummary();
    }
  }, [fetchPrompts, fetchSummary, sessionId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset fetch flag when sessionId changes
  useEffect(() => {
    summaryFetchedRef.current = false;
  }, [sessionId]);

  return useMemo(() => {
    return {
      // Loading states
      isLoadingPrompts,
      isLoadingSummary,
      isPending,
      summaryInProgress,

      // Data
      availablePrompts,
      summary: summary || streamingText,
      summaryExists,

      // Available models
      availableModels: availableSummaryModels,

      // Actions
      startSummarize,
      initSummarize,
      fetchSummary,
      fetchPrompts,
    };
  }, [
    isLoadingPrompts,
    isLoadingSummary,
    isPending,
    summaryInProgress,
    availablePrompts,
    summary,
    streamingText,
    summaryExists,
    startSummarize,
    initSummarize,
    fetchSummary,
    fetchPrompts,
  ]);
}
